import axios, { AxiosProgressEvent } from "axios";
import * as tus from "tus-js-client";

import {
  getImageUploadUrl,
  getTrackUploadUrl,
  getVideo,
  getVideoUploadUrl,
} from "@/data/api";
import { FileUploadType, Video } from "@/data/types";

import { LGT_SERVER_URL } from "./env";

export const FileUploadAcceptTypes = {
  [FileUploadType.IMAGE]:
    "image/png,image/jpeg,image/webp,image/gif,image/svg+xml",
  [FileUploadType.VIDEO]:
    "video/*,video/mp4,video/mov,video/avi,video/webm,video/mpg,video/mkv",
  [FileUploadType.AUDIO]:
    // be very prescriptive about web-safe audio formats so our player is guaranteed to work
    "audio/mpeg,audio/wav,audio/vorbis,audio/ogg,audio/flac,audio/aac,audio/mp4",
};

export const MaxFileSizeBytes = {
  CloudflareImage: 10485760, // 10MB
  CloudflareVideo: 209715200, // 200MB
  CloudflareTusVideo: 32212254720, // 30GB
};

export const uploadImageToCloudflare = async (
  file: File,
  onUploadProgress: (event: AxiosProgressEvent) => void,
) => {
  const formData = new FormData();
  formData.append("file", file, file.name);

  const { uploadURL } = await getImageUploadUrl();

  const response = await axios.post(uploadURL, formData, {
    onUploadProgress,
  });

  return response.data;
};

export const uploadVideoToCloudflare = async (
  file: File,
  onUploadProgress: (event: AxiosProgressEvent) => void,
) => {
  const formData = new FormData();
  formData.append("file", file, file.name);

  const { uploadURL, uid } = await getVideoUploadUrl();

  await axios.post(uploadURL, formData, {
    onUploadProgress,
  });

  return { result: { uid } };
};

export const uploadVideoToCloudflareWithTus = async (
  file: File,
  onUploadProgress: (bytesUploaded: number, bytesTotal: number) => void,
) => {
  return new Promise<{
    uid: string | undefined;
  }>((resolve, reject) => {
    let uid: string | undefined;

    new tus.Upload(file, {
      endpoint: `${LGT_SERVER_URL}/videos/tus_upload_url`,
      metadata: {
        filename: file.name,
        filetype: file.type,
        name: file.name,
      },
      onError: (event) => {
        reject(event);
      },
      onProgress: onUploadProgress,
      onSuccess: () => {
        resolve({ uid });
      },
      onBeforeRequest: (req) => {
        const url = req.getURL();
        // only set authorization for LGT Server endpoint to create upload
        // the Cloudflare endpoint from the create upload will reject
        // the Authorization header and fail the upload
        if (url.includes(LGT_SERVER_URL)) {
          req.setHeader(
            "Authorization",
            `Bearer ${localStorage.getItem("token")}`,
          );
        }
      },
      // @ts-expect-error unused variable req
      onAfterResponse: (req, res) => {
        uid = res.getHeader("stream-media-id");
      },
    }).start();
  });
};

export const uploadTrackToCloudflareR2 = async (
  file: File,
  onUploadProgress: (event: AxiosProgressEvent) => void,
) => {
  const { url, object_key, uid } = await getTrackUploadUrl(file.name);

  // file should be the only thing in the body for R2 S3 compatible uploads
  // otherwise, the upload becomes multipart/form-data in the bucket
  // and downloads will have multipart/form-data header instead of audio/[mp3, ogg, etc]
  await axios.put(url, file, {
    headers: {
      // Cloudflare returns this back as the content type during download
      // the browser would open music/video files instead of downloading them
      "Content-Type": file.type,
    },
    onUploadProgress,
  });

  return { result: { object_key, uid } };
};

export const pollForVideoProcessing = async (id: number) => {
  return new Promise<Video>((resolve, reject) => {
    const interval = setInterval(async () => {
      try {
        const video = await getVideo(id);
        if (video.ready_to_stream) {
          clearInterval(interval);
          resolve(video);
        }
      } catch (error) {
        clearInterval(interval);
        reject(error);
      }
    }, 5000);
  });
};
